import { definedMap } from "@glideapps/ts-necessities";

import { GlideFunctionalRenderer } from "common/components/uis";
import {
    PotentialValue,
    RenderEnvironment,
    LOADING,
    Context,
    asMaybeAction,
    asUI,
    ForceValueProducer,
    ActionSource,
    ValueProducer,
} from "common/components/types";
import {
    asArrayValue,
    loadedDefinedMapWithDefault,
    asString,
    loadedDefinedMap,
    asBoolean,
    isLoadedDefined,
} from "common/components/component-helpers";
import List from "../ui/smart/list/list";
import { makeOnClickFromAction } from "../ui/deprecated/components";
import { ListGroup } from "webapp/ui/smart/list/list-types";
import { mapFilterUndefined } from "common/Support";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useNetworkStatus } from "hooks/use-network-status";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";

export function makeListItems(
    list: PotentialValue,
    isGroupedPV: PotentialValue,
    force: ForceValueProducer,
    ctx: Context,
    env: RenderEnvironment,
    onLine: boolean
): { items?: readonly ValueProducer[]; groups?: readonly ListGroup<ValueProducer>[]; isEmpty: boolean } | undefined {
    const isGrouped = loadedDefinedMap(isGroupedPV, asBoolean) ?? false;

    let items: readonly ValueProducer[] | undefined;
    let groups: readonly ListGroup<ValueProducer>[] | undefined;

    const arr = loadedDefinedMap(list, asArrayValue) ?? [];
    let isEmpty: boolean;

    if (isGrouped) {
        groups = mapFilterUndefined(arr, groupVP => {
            const groupInspector = groupVP.getObjectInspector(force);
            if (!isLoadedDefined(groupInspector)) return undefined;

            const title = loadedDefinedMap(
                definedMap(groupInspector.getProperty("name") ?? groupInspector.getProperty("key"), force),
                asString
            );
            const itemsObj = loadedDefinedMap(definedMap(groupInspector.getProperty("items"), force), asArrayValue);
            const onSeeAllVP = groupInspector.getProperty("onSeeAll");
            const onSeeAllPV = definedMap(onSeeAllVP, force);
            const action = asMaybeAction(onSeeAllPV);
            // FIXME: We're using the `reallyNeedsOnline` flag from
            // `needsOnline` here, which is not necessarily correct.  We might
            // be enabling actions that shouldn't be.  Doing the right thing
            // is not trivial here, though, it seems.
            const isEnabled = action !== undefined && (onLine || !action.needsOnline(ctx, force, env)[1]);

            const onSeeAllCB = makeOnClickFromAction(definedMap(onSeeAllVP, force), ActionSource.None, ctx, env);
            const onSeeAll = isEnabled ? onSeeAllCB : undefined;

            if (itemsObj === undefined || itemsObj.length === 0) return undefined;

            return {
                title: title ?? "",
                items: itemsObj,
                onSeeAll: definedMap(onSeeAll?.onClick, h => () => h(false)),
            };
        });
        isEmpty = groups.length === 0;
    } else {
        items = arr;
        isEmpty = items.length === 0;
    }

    return { items, groups, isEmpty };
}

export const ListMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force, dataChanged] = useForce();
    const [
        [list, itemUI, caption, emptyMessage, onSeeAllPV, isGroupedPV, allowWrapping],
        ,
        valueProducersChanged,
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const onLine = useNetworkStatus();
    const { onClick } = useOnClickFromAction(onSeeAllPV, ActionSource.None, ctx, env);

    const fromData = dataChanged || valueProducersChanged;

    if (list === LOADING || itemUI === LOADING) return null;

    const captionValue = loadedDefinedMapWithDefault(caption, "", asString);
    const emptyMessageValue = loadedDefinedMap(emptyMessage, asString);

    const listItems = makeListItems(list, isGroupedPV, force, ctx, env, onLine);
    if (listItems === undefined) return null;

    const allowWrappingValue = loadedDefinedMapWithDefault(allowWrapping, false, asBoolean);

    return (
        <List
            title={captionValue}
            topAlign={false}
            appFeatures={env.appEnvironment.appFeatures}
            hasUnevenRows={allowWrappingValue}
            items={listItems.items}
            groups={listItems.groups}
            forceUpdate={fromData}
            emptyMessage={emptyMessageValue}
            onSeeAllClick={onClick}>
            {(item, i) => asUI(itemUI).render(item, env.with({ needsPush: i === "0-0" ? env.needsPush : false }), i)}
        </List>
    );
};
